import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useI18n, type MessageKey } from '@/i18n'
import { enqueueCommand } from '@/offline'
import { BilingualLabel, Button, ChoiceGroup, Sheet, useToast } from '@/ui'
import { CANCEL_REASONS, type CancelRunPayload } from '../../commands'
import { runParentIds, type RunView } from '../../projection'
import { canCancel } from '../../model/rules'

/** Cancel a run opened by mistake — only before any bag went in (F6.1). */
export function CancelRun({ run }: { run: RunView }) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<CancelRunPayload['reason'] | null>(null)
  const [saving, setSaving] = useState(false)
  if (!canCancel(run)) return null

  const cancel = async () => {
    if (!reason) return
    setSaving(true)
    try {
      await enqueueCommand({
        type: 'cancel_run',
        payload: { run_id: run.id, reason } satisfies CancelRunPayload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      toast.success(t('process.cancel.done'))
      navigate('/m/process', { replace: true })
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button variant="ghost" size="touch" fullWidth onClick={() => setOpen(true)}>
        <BilingualLabel k="process.cancel.button" />
      </Button>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={<BilingualLabel k="process.cancel.button" />}
        description={t('process.cancel.hint')}
        footer={
          <Button variant="danger" size="xl" fullWidth disabled={!reason} loading={saving} onClick={() => void cancel()}>
            <BilingualLabel k="process.cancel.confirm" />
          </Button>
        }
      >
        <ChoiceGroup
          layout="stack"
          options={CANCEL_REASONS.map((r) => ({ value: r, label: t(`process.cancel.reason.${r}` as MessageKey) }))}
          value={reason}
          onChange={setReason}
        />
      </Sheet>
    </>
  )
}
